import { Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { Chart } from "react-google-charts";
import {
  ConvertTimespanToDateBasedOnTimezone,
  getBetweenTime,
  GetCustomerCurrentTime,
  GetTimeWithStyle,
} from "../../../../services/common/cnc_apis";

import { useSelector } from "react-redux";
import lang from "../../../../services/lang";
import CommentMgrDlg from "../../CommentMgrDlg/CommentMgrDlg";
import "./OneGantt.css";

function OneGanttShift(props) {
  const { langIndex } = useSelector((x) => x.app);
  const { ganttInfo, security_level } = props;
  const { machineInfo, customer_id } = props;
  const { customerInfo, currentDate } = props;
  const { myGanttList, setMyGanttList } = props;

  const [selectedChatItem, setSelectedChatItem] = useState("");
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const chartWrapperRef = useRef(null);

  useEffect(() => {
    return () => {
      try {
        const wrapper = chartWrapperRef.current;
        if (wrapper && typeof wrapper.getChart === "function") {
          const chart = wrapper.getChart();
          if (chart && typeof chart.clearChart === "function") chart.clearChart();
        }
      } catch (_) {}
      chartWrapperRef.current = null;
    };
  }, []);

  let ganttData = [];
  let identificationList = [];
  let color_array = [];
  let status_array = [];
  let shiftStartDate = null;
  let shiftEndDate = null;


  const columns = [
    { type: "string", id: "Position" },
    { type: "string", id: "President" },
    { type: "string", role: "tooltip", id: "tmp", p: { html: true } },
    { type: "datetime", id: "Start" },
    { type: "datetime", id: "End" },
  ];

  // Always calculate shift dates for viewWindow, even if there's no gantt data
  const timezone = customerInfo["timezone"];
  if (machineInfo && machineInfo["shift_start"] && machineInfo["shift_end"]) {
    let startDate = ConvertTimespanToDateBasedOnTimezone(machineInfo["shift_start"] / 1000, timezone);
    let endDate = ConvertTimespanToDateBasedOnTimezone(machineInfo["shift_end"] / 1000, timezone);
    
    // Handle cross-day shifts (e.g., 18:00 to 05:00 next day)
    // If endDate is before or equal to startDate, it means the shift crosses midnight
    if (endDate <= startDate) {
      // Add 24 hours to endDate to make it the next day
      endDate = new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
    }
    
    // Store shift boundaries for chart options
    shiftStartDate = startDate;
    shiftEndDate = endDate;
  }

  if (ganttInfo !== undefined && ganttInfo.length > 0) {
    // Filter out items where end equals start (zero duration items that cause duplicate lines)
    // This should be done before any other processing
    const validGanttInfo = ganttInfo.filter((item) => {
      // Skip items where end is equal to start (zero duration)
      if (item["end"] && item["start"] && item["end"] === item["start"]) {
        return false;
      }
      // Also check after timezone conversion to catch edge cases
      const itemStart = ConvertTimespanToDateBasedOnTimezone(item["start"], timezone);
      if (item["end"] && item["end"] !== 0 && item["end"] !== null && item["end"] !== undefined) {
        const itemEnd = ConvertTimespanToDateBasedOnTimezone(item["end"], timezone);
        // If end equals start after conversion, filter it out
        if (itemEnd.getTime() === itemStart.getTime()) {
          return false;
        }
      }
      return true;
    });

    // Get current time to cap ongoing items and prevent future data
    const currentTime = GetCustomerCurrentTime(timezone);

    // Helper function to normalize end time
    // Handles: ongoing items (end is 0/null/undefined), midnight boundary cases, and future data
    // CRITICAL: Always caps to current time to prevent ANY future data
    const normalizeEndTime = (startTimestamp, endTimestamp, shiftEndDate, currentTimeDate) => {
      const startDate = ConvertTimespanToDateBasedOnTimezone(startTimestamp, timezone);
      
      // Check if end is 0, null, undefined, or invalid (ongoing item)
      if (!endTimestamp || endTimestamp === 0 || endTimestamp === null || endTimestamp === undefined) {
        // For ongoing items, ALWAYS use current time (never shift end if it's in the future)
        return currentTimeDate;
      }
      
      let endDate = ConvertTimespanToDateBasedOnTimezone(endTimestamp, timezone);
      
      // If end is before or equal to start, it could be:
      // 1. Midnight boundary case (same day, end is 00:00:00) - add 24 hours
      // 2. Invalid data - cap to current time
      if (endDate <= startDate) {
        // Check if this is a reasonable midnight boundary (within 1 hour of start)
        const timeDiff = Math.abs(startDate.getTime() - endDate.getTime());
        if (timeDiff < 60 * 60 * 1000) {
          // Likely a midnight boundary case, add 24 hours
          endDate = new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
        } else {
          // Invalid data, cap to current time
          endDate = currentTimeDate;
        }
      }
      
      // CRITICAL: Cap to current time FIRST (most important - no future data)
      if (endDate > currentTimeDate) {
        endDate = currentTimeDate;
      }
      
      // Then cap to shift end if needed (but current time is already the limit)
      if (endDate > shiftEndDate) {
        endDate = shiftEndDate;
      }
      
      return endDate;
    };

    // Filter gantt items to only include those that overlap with the shift period
    // An item overlaps if: (itemStart < shiftEndDate) AND (itemEnd > shiftStartDate)
    // Also ensure items don't extend beyond current time (no future data)
    // Use validGanttInfo (already filtered for zero-duration items) instead of ganttInfo
    const filteredGanttInfo = validGanttInfo.filter((item) => {
      const itemStart = ConvertTimespanToDateBasedOnTimezone(item["start"], timezone);
      
      // Check if end is 0, null, undefined, or invalid (ongoing item)
      let itemEnd;
      if (!item["end"] || item["end"] === 0 || item["end"] === null || item["end"] === undefined) {
        // Ongoing item - use current time (never shift end, to prevent future data)
        itemEnd = currentTime;
      } else {
        itemEnd = ConvertTimespanToDateBasedOnTimezone(item["end"], timezone);
        
        // Normalize end time if needed (midnight boundary case)
        if (itemEnd <= itemStart) {
          const timeDiff = Math.abs(itemStart.getTime() - itemEnd.getTime());
          if (timeDiff < 60 * 60 * 1000) {
            // Likely a midnight boundary case, add 24 hours
            itemEnd = new Date(itemEnd.getTime() + 24 * 60 * 60 * 1000);
          } else {
            // Invalid data, cap to current time
            itemEnd = currentTime;
          }
        }
        
        // CRITICAL: Cap to current time to prevent ANY future data
        if (itemEnd > currentTime) {
          itemEnd = currentTime;
        }
        
        // Also cap to shift end if needed
        if (shiftEndDate && itemEnd > shiftEndDate) {
          itemEnd = shiftEndDate;
        }
      }
      
      // Check if item overlaps with shift period AND doesn't start in the future
      // Item must start before or at current time, and end after shift start
      return itemStart <= currentTime && 
             shiftEndDate && itemStart < shiftEndDate && 
             shiftStartDate && itemEnd > shiftStartDate;
    });

    // If no items overlap with shift, don't add any segments
    // The viewWindow will show the full time scale (shift start to shift end)
    // but we don't need to add empty segments that might render as visible bars
    if (filteredGanttInfo.length === 0) {
      // Don't add any segments - let the viewWindow show the time scale
      // If we need to show something, we could add a minimal segment, but it's better to show nothing
    } else {
      // DO NOT add "start" segment - transparent segments can render as visible bars
      // The viewWindow will show the full time scale anyway

      // Use a Map to track status->color mapping for items actually added to ganttData
      // This ensures colors match the items that are actually displayed
      const statusColorMap = new Map();
      
      // Add filtered gantt items, clipping them to shift boundaries and current time
      filteredGanttInfo.forEach((item) => {
        let itemStart = ConvertTimespanToDateBasedOnTimezone(item["start"], timezone);
        let itemEnd = normalizeEndTime(item["start"], item["end"], shiftEndDate, currentTime);
        
        // CRITICAL: Clip item start to not be in the future
        if (itemStart > currentTime) {
          // Skip items that start in the future
          return;
        }
        
        // Clip item to shift boundaries
        if (shiftStartDate && itemStart < shiftStartDate) {
          itemStart = shiftStartDate;
        }
        
        // CRITICAL: Clip end to current time FIRST (most important)
        if (itemEnd > currentTime) {
          itemEnd = currentTime;
        }
        
        // Then clip to shift end if needed
        if (shiftEndDate && itemEnd > shiftEndDate) {
          itemEnd = shiftEndDate;
        }
        
        // FINAL SAFETY CHECK: Ensure both start and end are not in the future
        // This is a double-check to prevent any future data from slipping through
        if (itemStart > currentTime || itemEnd > currentTime) {
          // Skip this item - it has future data
          return;
        }
        
        // Only add if there's a valid time range after clipping
        // AND the item doesn't start in the future
        if (itemEnd > itemStart && itemStart <= currentTime && itemEnd <= currentTime) {
          // Calculate tooltip end time (original, not clipped, but capped to current time)
          let tooltipEnd = normalizeEndTime(item["start"], item["end"], shiftEndDate, currentTime);
          if (tooltipEnd > currentTime) {
            tooltipEnd = currentTime;
          }
          
          // FINAL VALIDATION: Ensure chart end time is never beyond current time
          const chartEndTime = itemEnd > currentTime ? currentTime : itemEnd;
          const chartStartTime = itemStart > currentTime ? currentTime : itemStart;
          
          // Only add if we have a valid range and no future data
          if (chartEndTime > chartStartTime && chartEndTime <= currentTime && chartStartTime <= currentTime) {
            ganttData.push([
              machineInfo["machine_id"],
              item["status"],
              createCustomHTMLContent(
                item["status"],
                ConvertTimespanToDateBasedOnTimezone(item["start"], timezone), // Use original start for tooltip
                tooltipEnd, // Use normalized end (capped to current time) for tooltip
                item["Operator"],
                getBetweenTime(item["start"], item["end"]),
                item["job_id"] === undefined ? "" : item["job_id"],
                item["comment"] === null || item["comment"] === undefined
                  ? ""
                  : item["comment"]
              ),
              chartStartTime, // Use validated start for chart (NEVER in future)
              chartEndTime, // Use validated end for chart (NEVER beyond current time)
            ]);
            identificationList.push(item);
            
            // Track status and color for items actually added to ganttData
            // This ensures the color mapping is correct for displayed items
            const statusKey = item["status"].toLowerCase();
            if (!statusColorMap.has(statusKey)) {
              statusColorMap.set(statusKey, { status: item["status"], color: item["color"] });
            }
          }
        }
      });

      // Add minimal invisible segments at shift boundaries to force the time scale to show full shift range
      // This ensures the viewWindow displays the complete shift period (shift start to shift end)
      // These segments use a background-matching color to be invisible
      // IMPORTANT: Add invisible segments BEFORE building status/color arrays to ensure correct color mapping
      if (shiftStartDate && shiftEndDate) {
        const invisibleColor = "#1a1a1a"; // Dark color matching typical dark theme background
        
        // Add segment at shift start if no data starts there
        if (ganttData.length === 0 || ganttData[0][3].getTime() > shiftStartDate.getTime()) {
          const invisibleStatusStart = "__scale_marker_start__";
          ganttData.unshift([ // Use unshift to add at the beginning
            machineInfo["machine_id"],
            invisibleStatusStart,
            "",
            shiftStartDate,
            new Date(shiftStartDate.getTime() + 1), // 1ms after shift start
          ]);
          identificationList.unshift("");
        }
        
        // Add segment at shift end if no data extends there
        if (ganttData.length > 0) {
          const lastItemEnd = ganttData[ganttData.length - 1][4]; // End time is at index 4
          if (lastItemEnd.getTime() < shiftEndDate.getTime()) {
            const invisibleStatusEnd = "__scale_marker_end__";
            ganttData.push([
              machineInfo["machine_id"],
              invisibleStatusEnd,
              "",
              new Date(shiftEndDate.getTime() - 1), // 1ms before shift end
              shiftEndDate, // Exactly at shift end
            ]);
            identificationList.push("");
          }
        } else {
          // If no data at all, add a segment spanning the full shift
          const invisibleStatusFull = "__scale_marker_full__";
          ganttData.push([
            machineInfo["machine_id"],
            invisibleStatusFull,
            "",
            shiftStartDate,
            shiftEndDate,
          ]);
          identificationList.push("");
        }
      }

      // Build status and color arrays based on the order statuses FIRST appear in ganttData
      // This is critical - Google Charts Timeline maps colors based on first appearance order
      const seenStatuses = new Set();
      for (let i = 0; i < ganttData.length; i++) {
        const status = ganttData[i][1]; // Status is at index 1
        const statusKey = status.toLowerCase();
        
        // Skip invisible scale markers - they use a fixed color
        if (status.startsWith("__scale_marker")) {
          if (!seenStatuses.has(statusKey)) {
            seenStatuses.add(statusKey);
            status_array.push(status);
            color_array.push("#1a1a1a"); // Dark color for invisible markers
          }
          continue;
        }
        
        // For real statuses, get color from the statusColorMap
        if (!seenStatuses.has(statusKey)) {
          seenStatuses.add(statusKey);
          if (statusColorMap.has(statusKey)) {
            const value = statusColorMap.get(statusKey);
            status_array.push(value.status);
            color_array.push(value.color);
          } else {
            // Fallback: try to find color from filteredGanttInfo
            const item = filteredGanttInfo.find((g) => g["status"].toLowerCase() === statusKey);
            if (item) {
              status_array.push(item["status"]);
              color_array.push(item["color"]);
            }
          }
        }
      }
    }
  }

  function createCustomHTMLContent(
    status,
    from,
    to,
    operator,
    duration,
    jobId,
    comment
  ) {
    return (
      '<div style="padding:5px 5px 5px 5px; background-color:#1e1e1e; color: #eeeeee; text-align: left;width: 220px; ">' +
      "<span>" +
      lang(langIndex, "cnc_status") +
      ": <b>" +
      status +
      "</b></span><br>" +
      "<span>" +
      lang(langIndex, "cnc_time") +
      ": <b>" +
      GetTimeWithStyle(from) +
      " - " +
      GetTimeWithStyle(to) +
      "</b></span><br>" +
      "<span>" +
      lang(langIndex, "cnc_duration") +
      ": <b>" +
      duration +
      "</b></span><br>" +
      "<span>" +
      lang(langIndex, "plant_operator") +
      ": <b>" +
      operator +
      "</b></span><br>" +
      "<span>" +
      lang(langIndex, "cnc_jobid") +
      ": <b>" +
      jobId +
      "</b></span><br>" +
      "<span>" +
      lang(langIndex, "cnc_comment") +
      ': </span><div style="margin-left:20px"><b>' +
      comment +
      "</b></div>" +
      "</div>"
    );
  }

  const chartEvents = [
    {
      eventName: "ready",
      callback({ chartWrapper }) {
        try {
          const prev = chartWrapperRef.current;
          if (prev && prev !== chartWrapper && typeof prev.getChart === "function") {
            const chart = prev.getChart();
            if (chart && typeof chart.clearChart === "function") chart.clearChart();
          }
        } catch (_) {}
        chartWrapperRef.current = chartWrapper;
      },
    },
    {
      eventName: "select",
      callback({ chartWrapper }) {
        if (customer_id === undefined) return;
        const selection = chartWrapper.getChart().getSelection();
        if (!selection || selection.length === 0) return;
        var selectedRow = selection[0].row;
        var identification = identificationList[selectedRow];
        setSelectedChatItem(identification);
        setIsVisibleModal(true);
      },
    },
  ];

  return (
    <div style={{ textAlign: "center", paddingTop: 15 }}>
      {isVisibleModal && (
        <CommentMgrDlg
          selectedChatItem={selectedChatItem}
          isVisibleModal={isVisibleModal}
          setIsVisibleModal={setIsVisibleModal}
          customer_id={customer_id}
          myGanttList={myGanttList}
          security_level={security_level}
          setMyGanttList={setMyGanttList}
          timezone={customerInfo["timezone"]}
          ganttInfo={ganttInfo}
          machineInfo={machineInfo}
        />
      )}

      {ganttInfo === undefined || ganttData.length === 0 ? (
        <Spin size="medium"></Spin>
      ) : (
        <div>
          <Chart
            className="one-line-timeline"
            height={100}
            width={"100%"}
            chartType="Timeline"
            data={[columns, ...ganttData]}
            chartEvents={chartEvents}
            options={{
              showRowNumber: false,
              showBarLabels: false,
              showName: false,
              timeline: { showRowLabels: false, showBarLabels: false },
              backgroundColor: "transparent",
              legend: "none",
              colors: color_array,
              allowHtml: true,
              tooltip: { isHtml: true },
              hAxis: {
                textStyle: {
                  color: "#FFFFFF",
                },
                gridlines: {
                  color: "#FFFFFF",
                },
                baselineColor: "#FFFFFF",
                // Force the time scale to show the full shift range (shift start to shift end)
                // Always set viewWindow when we have shift dates to ensure proper time scale
                ...(shiftStartDate && shiftEndDate ? {
                  viewWindow: {
                    min: shiftStartDate,
                    max: shiftEndDate,
                  },
                } : {}),
              },
            }}
          />
        </div>        
      )}
    </div>
  );
}

export default OneGanttShift;

